import type { ReactNode, RefObject } from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";
import { POPUP_CLASSNAME } from "constants/common.constants";

type ModalProps = {
  title: string;
  /** Sits under the title, as the design's `pcmodal__t` line. */
  subtitle?: string;
  /**
   * Buttons for `.pcmodal__acts`, in the order the design lists them. The
   * bigger modals put theirs in the header instead — see `headerActions`.
   */
  actions?: ReactNode;
  /**
   * Buttons beside the title rather than below the content. The design uses
   * this on every modal wide enough for its body to scroll, so Close stays
   * reachable without scrolling back up.
   */
  headerActions?: ReactNode;
  onClose: () => void;
  /** `wide` matches the design's `--wide` box. */
  wide?: boolean;
  /**
   * As a function, it is handed the dialog box, so a dropdown inside the modal
   * can measure against it and open upward instead of hanging out the bottom.
   */
  children?: ReactNode | ((box: RefObject<HTMLDivElement | null>) => ReactNode);
};

/**
 * Renders into the node the bundle appends next to the app root, so a modal is
 * never trapped inside a stacking context created further up the host page.
 */
export const Modal = ({ title, subtitle, children, actions, headerActions, onClose, wide }: ModalProps) => {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const titleId = `pcmodal-${title.replace(/\W+/g, "-").toLowerCase()}`;

  useEffect(() => {
    setHost(document.querySelector<HTMLElement>(`.${POPUP_CLASSNAME}`));
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (host === null) {
    return null;
  }

  const heading = (
    <>
      <div className="pcmodal__h" id={titleId}>
        {title}
      </div>
      {subtitle !== undefined && <p className="pcmodal__t">{subtitle}</p>}
    </>
  );

  return createPortal(
    <div className="pcmodal">
      <div className="pcmodal__back" onClick={onClose} />
      <div
        ref={boxRef}
        className={cn("pcmodal__box", { "pcmodal__box--wide": wide })}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {headerActions === undefined ? (
          heading
        ) : (
          <div className="pccg__top">
            <div>{heading}</div>
            <div className="pccg__top-a">{headerActions}</div>
          </div>
        )}

        {typeof children === "function" ? children(boxRef) : children}

        {actions !== undefined && <div className="pcmodal__acts">{actions}</div>}
      </div>
    </div>,
    host
  );
};
